import {action, computed, makeObservable, observable, runInAction} from 'mobx';
import {AiRequester} from "../MobxStores/RequestApi/AiRequester"
import {UPLOAD_CHUNK_SIZE} from "../Utility";
import ParsingUtility from "../Utilities/FileParsingUtility/ParsingUtility";
import EmbedFilesUtility from "../Utilities/FileParsingUtility/EmbedFilesUtility";
import {AllowedFileExtensionsAiChatUploads} from "../serverClientShared/sharedEnums";
import TranscribeVideoUtility from "../Utilities/FileParsingUtility/TranscribeVideoUtility";
import {handleAbort} from "../Utilities/FileParsingUtility/PdfParser";

export class AiStudioStore {
    workspace = '';
    totalFiles;
    abortedFiles = new Set()
    hasPendingChanges = false;
    constructor(aiStore, generalStore, modalStore, projectStore) {
        makeObservable(this, {
            workspace: observable,
            abortedFiles: observable,
            hasPendingChanges: observable,
            isAbortAll: computed,
            handleFilesUploads: action,
            openChooseRoleModal:action,



        });
        this.aiStore = aiStore;
        this.generalStore = generalStore;
        this.modalStore = modalStore;
        this.projectStore = projectStore;
        this.workspace = generalStore.workspace;
    }
    get isAbortAll() {
        return this.abortedFiles.has('ABORT ALL');
    }
    parseFiles = async(files, isFromLibrary)=> {
        const parsingPromises = files.map(async (file, i) => {
            const fileName = file.OriginalFileName ? file.OriginalFileName + '.' + file.FileExtension : file.name ?? file.FileName + '.' + file.FileExtension;
            try {
                const result = await ParsingUtility.parseFileByType(file, isFromLibrary, true, this.abortedFiles);
                const {messages, parsedResult} = result;
                if (!parsedResult){
                    throw new Error('failed parsing file');
                }
                return {messages,parsedResult};
            } catch (err) {
                this.modalStore.showErrorMessage("Failed to parse file: " + fileName);
                return null;
            }
        });
        const parsedFilesMessage = await Promise.all(parsingPromises);
        return parsedFilesMessage.filter(Boolean);
    }
    allowedFileTypesToUpload = [
        AllowedFileExtensionsAiChatUploads.PDF,
        AllowedFileExtensionsAiChatUploads.DOCX,
        AllowedFileExtensionsAiChatUploads.TXT,
        AllowedFileExtensionsAiChatUploads.MP4,
        AllowedFileExtensionsAiChatUploads.VIDEO
    ];
    async embedFiles(files, parsedFiles, originalFiles, onProgress, index) {
        const isVideo = files.some(file => file.FileExtension === 'mp4');
        if (isVideo) {
            const transcriber = await TranscribeVideoUtility.getModelConnection('web');
        }
        const embedder = await EmbedFilesUtility.getModelConnection('web');
        const parsingPromises = files.map(async (file, i) => {
            const fileName = file.OriginalFileName ? file.OriginalFileName + '.' + file.FileExtension : file.name ?? file.FileName + '.' + file.FileExtension;
            try {
                const embeddedVectors =  await EmbedFilesUtility.embedFileByType(file,parsedFiles[i].parsedResult, originalFiles[i], onProgress, index, this.abortedFiles);
                if (!embeddedVectors){
                    throw new Error('failed embedding file');
                };
                if (handleAbort({name:''}, this.abortedFiles, true)) {
                    return []
                }
                return embeddedVectors;
            } catch (err) {
                this.modalStore.showErrorMessage("Failed to embedding file: " + fileName);
                return null;
            }
        });
        const embededFiles = await Promise.all(parsingPromises);
        return embededFiles;
    }
    filterFiles(files, MAX_FILE_SIZE_BYTES= 50*1024*1024) {
        return files.filter(file => {
            const ext = file.name.split('.').pop().toLowerCase();
            const isAllowed = this.allowedFileTypesToUpload.some(type => type.toLowerCase() === ext);
            const isSizeOk = file.size <= MAX_FILE_SIZE_BYTES;
            return isAllowed && isSizeOk;
        });
    }
    updateLoader(step, message, i) {
        if (!message){
            message = this.generalStore.progressLoaderModalWithMassageState.message
        }
        const value = Math.min(100, Math.floor(((i + step) / this.totalFiles) * 90 + 10));

        this.generalStore.progressLoaderModalWithMassageState = {
            message,
            value
        };
    }

    handleFilesUploads = async (files) => {
        if (files.length === 0) return;
        const filteredFiles = this.filterFiles(files);
        if (filteredFiles.length > 10) {
            this.modalStore.showErrorMessage('Maximum of 10 files per upload.', false)
            return;
        }if (filteredFiles.length < files.length) {
            this.modalStore.showErrorMessage('Some files were not uploaded. The maximum file size is 50MB, and only PDF, DOCX, TXT, and video files are allowed.', false)
        }
        if (filteredFiles.length === 0) return;
        this.generalStore.isBlockGeneralLinearModal = true
        this.generalStore.progressLoaderModalWithMassageState = {message: 'Uploading Files', value: 0};
        this.generalStore.isProgressLoaderModalWithMassage = true

        const uploadedFilesAndPages = [];
        const total = filteredFiles.length;
        this.totalFiles = total

        try {
            if (this.isAbortAll) {
                throw new Error('Upload aborted');
            }
            const associatedFiles = await this.uploadFiles(filteredFiles, false);
            this.generalStore.progressLoaderModalWithMassageState = {
                message: `Uploading files`,
                value: 10,
            };

            for (let i = 0; i < total; i++) {
                const file = filteredFiles[i];
                try {
                    if (this.isAbortAll) {
                        throw new Error('Upload aborted');
                    }
                    const associatedFile = [associatedFiles[i]]
                    this.updateLoader(0.25, `Parsing ${file.name}`, i)
                    if (this.isAbortAll) {
                        throw new Error('Upload aborted');
                    }
                    const parsedFile = await this.parseFiles([file], false);
                    this.updateLoader(0.50, `Embedding ${file.name}`, i)
                    if (!parsedFile){
                        throw new Error('failed to upload File');
                    }
                    if (this.isAbortAll) {
                        throw new Error('Upload aborted');
                    }
                    const boundUpdateLoader = (step, message, i) => this.updateLoader(step, message, i);
                    const embeddedFile = await this.embedFiles(
                        associatedFile, parsedFile, [file], boundUpdateLoader,  i
                    );
                    if(!embeddedFile){
                        throw new Error('failed to upload file');
                    }
                    if (this.isAbortAll) {
                        throw new Error('Upload aborted');
                    }

                    this.updateLoader(0.75,`Saving ${file.name}`, i)

                    const savedFile = await AiRequester.uploadEmbeddedFiles(
                        this.workspace,
                        associatedFile,
                        embeddedFile
                    );

                    uploadedFilesAndPages.push(savedFile);

                    this.updateLoader(1,`Done ${file.name}`, i)
                } catch (e) {
                    if (this.isAbortAll) {
                        throw new Error('Upload aborted');
                    }
                    this.modalStore.showErrorMessage(`Error Uploading ${file.name ?? 'file'}`);
                }
            }

            if (this.isAbortAll) {
                throw new Error('Upload aborted');
            }

            this.generalStore.progressLoaderModalWithMassageState = {
                message: 'All files processed',
                value: 100,
            };
            await new Promise((resolve) => setTimeout(resolve, 800));
            this.modalStore.showSuccessMessage('Successfully uploaded files.');
            this.hasPendingChanges = true;
            return uploadedFilesAndPages;
        } catch (e) {
            this.modalStore.showErrorMessage(`${this.isAbortAll ? 'Upload canceled':'Error Uploading Files'}`);
        } finally {
            this.generalStore.progressLoaderModalWithMassageState = {message: '', value: 0};
            this.generalStore.isProgressLoaderModalWithMassage = false
            this.generalStore.progressLoaderValue = null
            this.generalStore.isBlockGeneralLinearModal = false
            this.abortedFiles.clear();
        }
    }
    uploadFiles = async (files, isSendProgressToChat) => {
        try {
            if (this.isAbortAll) {
                throw new Error('Upload aborted');
            }
            this.generalStore.isProgressLoaderLinear = true
            const filesAfterCompression = await this.aiStore.compressFiles(files, this.abortedFiles);
            if (this.isAbortAll) {
                throw new Error('Upload aborted');
            }
            const uploadedCounter = [0];
            const totalChunks = filesAfterCompression.reduce((accumulator, file) => Math.ceil(file.size / UPLOAD_CHUNK_SIZE) + accumulator, 0);
            this.isLoaderOpen = false;
            const promises = filesAfterCompression.map((file, i) => this.generalStore.uploadFile(file, totalChunks, uploadedCounter, this.projectStore.currentProject.ProjectIdentifier, 'root', null, null, i, true).then((result) => {
                this.generalStore.progressLoaderModalWithMassageState = {message: 'Uploading files', value: this.generalStore.progressLoaderModalWithMassageState.value+ 30/files.length};
                if (this.abortedFiles.has('ABORT ALL')) {
                    throw('aborted All Files');
                }
                return result;
            }));
            const associatedFiles = await Promise.all(promises);
            return associatedFiles;
        } catch (e) {
            this.modalStore.showErrorMessage(`${this.isAbortAll ? 'Upload canceled' : files.length > 1 ? 'Error Uploading Files' : `Error Uploading File: ${files[0].name}`}`);
        }
    };

   openChooseRoleModal = (e, chooseInitRoleState, handleSaveRolePopup, isBatch = false) => {
        const popupWidth = 332;
        this.modalStore.chooseInitRoleState = chooseInitRoleState;
        this.modalStore.handleSaveRolePopup = handleSaveRolePopup;
        this.modalStore.modalData.isBatch = isBatch;
        this.modalStore.chooseRolePopupPosition = isBatch
            ? this.getBatchRolePopupPosition(e, popupWidth)
            : this.getRolePopupPosition(e, popupWidth);
        this.modalStore.isChooseRolePopupOpen = true;
    }

    getBatchRolePopupPosition = (e, popupWidth) => {
        const rect = e?.currentTarget?.getBoundingClientRect() || {left: 400, top: 400};
        const x = Math.max(0, Math.min(rect.left + window.scrollX, document.documentElement.clientWidth - popupWidth));
        return {x, y: rect.top + window.scrollY - 10, anchorBottom: true};
    }

    getRolePopupPosition = (e, popupWidth) => {
        const popupHeight = 125;
        let y = e.clientY + window.scrollY;
        const spaceBelow = window.innerHeight - e.clientY;
        if (spaceBelow < popupHeight && e.clientY >= popupHeight) {
            y = e.clientY + window.scrollY - popupHeight;
        }
        const x = Math.max(0, Math.min(e.clientX + window.scrollX, document.documentElement.clientWidth - popupWidth));
        return {x, y: Math.max(0, y)};
    }
}